/**
 * dextoro fee policy constants. Single source of truth.
 *
 * Canonical home: supabase/functions/_shared/feeShares.ts (Deno-importable).
 * Mirrored to: src/lib/constants/feeShares.ts (Vite-bundled frontend)
 *           and dextoro-admin/src/lib/constants/feeShares.ts.
 * CI step asserts the three files are byte-equal on every PR.
 *
 * On-chain pool config (cliff_fee_numerator, protocol_fee_percent,
 * referral_fee_percent) is read from each pool via readDammV2Pool. These
 * constants are for:
 *   - Display fallback when pool config isn't loaded yet.
 *   - Splitting LP fees into creator share vs dextoro share (a dextoro
 *     business decision, not on-chain).
 *   - Marketing/UI copy of expected rates.
 *
 * NEVER inline these literals elsewhere. Marketing string "1.4%" is exempt
 * (lives in user-facing copy per feedback_keep_1_4_percent memory).
 *
 * Verification basis (PR 2 STEP 1, 2026-05-03):
 *   cp-amm Rust source split_fees confirms referral_fee is a sub-cut of
 *   protocol_fee, only deducted when a referrer is passed on the swap ix.
 *   Jupiter Swap V1 routes for cp-amm v2 do not pass a referrer, so LP
 *   receives 100 - protocol_fee_percent percent of the total fee. The
 *   referral_fee_percent on-chain config field has zero current effect.
 *   Tracked: issues-queue O15 (register dextoro as referrer in Jupiter).
 *
 * Empirically verified on Toro / BULLY / MUSLIM positions (2026-05-03):
 *   creator position permanent_locked_liquidity / total = 70%
 *   treasury position permanent_locked_liquidity / total = 30%
 */

// — Share splits —
export const CREATOR_SHARE_BPS_BONDING    = 3000;  // BC: creator gets 30% of LP fees
export const DEXTORO_SHARE_BPS_BONDING    = 7000;  // BC: dextoro gets 70% of LP fees
export const CREATOR_SHARE_BPS_GRADUATED  = 7000;  // graduated: creator gets 70% (shares invert at graduation)
export const DEXTORO_SHARE_BPS_GRADUATED  = 3000;  // graduated: dextoro gets 30%
export const BPS_DENOMINATOR              = 10000;

// — Default total / LP rates (V4) —
// Total = cliff_fee_numerator / FEE_DENOMINATOR.
// LP    = total * (100 - protocol_fee_percent) / 100.
// Read from chain when possible; these are display fallback only.
export const TOTAL_FEE_RATE_DEFAULT       = 0.020;   // 2.00% V4 cliff
export const LP_RATE_DBC_DEFAULT          = 0.016;   // BC LP-only after Meteora's 20% protocol cut
export const LP_RATE_DAMM_V2_DEFAULT      = 0.016;   // DAMM V2 LP-only (same shape post-V4)

// — Derived effective rates (no hardcoded literals) —
export const CREATOR_RATE_BONDING_DEFAULT     = LP_RATE_DBC_DEFAULT     * (CREATOR_SHARE_BPS_BONDING   / BPS_DENOMINATOR); // 0.0048
export const DEXTORO_EFFECTIVE_RATE_BONDING   = LP_RATE_DBC_DEFAULT     * (DEXTORO_SHARE_BPS_BONDING   / BPS_DENOMINATOR); // 0.0112
export const CREATOR_RATE_GRADUATED_DEFAULT   = LP_RATE_DAMM_V2_DEFAULT * (CREATOR_SHARE_BPS_GRADUATED / BPS_DENOMINATOR); // 0.0112
export const DEXTORO_EFFECTIVE_RATE_GRADUATED = LP_RATE_DAMM_V2_DEFAULT * (DEXTORO_SHARE_BPS_GRADUATED / BPS_DENOMINATOR); // 0.0048
